// Ported from dev-scrips-pm-react src/documents/services/use-save-document.ts (PROD-1119 documents module).
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { track } from '@/shared/lib/analytics';
import { useAuthStore } from '@/auth/auth-store';
import type { PatientDocumentModel } from '@/documents/types/document';

/**
 * POST /api/Patients/AddUpdatePatientDocument
 *
 * Mirrors Flutter `DocumentDataSourceImpl.addUpdatePatientDocuments`
 * (`document_data_source_impl.dart:43-57`). Wire body dev-curl verified
 * 2026-05-15 by Andrew.
 *
 * Body contract for a CREATE (no `id` field, or `id: null`):
 *   - `patientId` REQUIRED (GUID)
 *   - `encounterId` optional — `null` is valid (verified curl)
 *   - `name`, `type`, `documentDate` REQUIRED per Flutter validation
 *   - Audit fields (`createdById` / `createdByName` / `createdOn` /
 *     `updatedById` / `updatedByName` / `updatedOn`) ship as `null`
 *     on create — server fills them in from the auth headers + now()
 *   - `data` SHIPS AS `{ title: null, code: null }` even when unused
 *     (Note / Form / Admin Referrals types don't read it; the verified
 *     curl shows it present-and-null, NOT omitted — keep it)
 *   - `documentDate` ships as `YYYY-MM-DDTHH:mm:ss.sss` (no `Z`
 *     suffix — .NET model binder rejects the UTC marker per the
 *     project's date-format convention)
 *   - `patientDocumentFiles[]` carries each uploaded file:
 *       - `url` = signed Azure-blob URL from useUploadDocument
 *       - `fileName` = original filename including extension
 *       - `fileType` = DOT-PREFIXED extension (e.g. `.png`, NOT
 *         MIME type — per verified curl)
 *       - `isDeleted: false` on create
 *   - `patientDocumentLabs` ships as `[]` (empty array, not null)
 *     for non-Lab types
 *
 * Body contract for an UPDATE (`id` present): same shape, all the
 * editable fields can be overwritten. Files retain their existing
 * URLs when not re-uploaded.
 *
 * Response: the saved document id (string).
 *
 * Cache: invalidates `['patient-documents']` so the Docs tab list
 * refetches automatically after save.
 */

/**
 * The backend ships the response as a **bare JSON string** (quoted
 * GUID) — verified against curl-4 in `docs/CONVERSIONS/lab-linkage/
 * CONVERSION_NOTES.md §15`:
 *
 *   "3401e133-660e-4d55-a685-550148a8b122"
 *
 * `apiClient.parseResponse` runs `JSON.parse` which yields the raw
 * string. The prior shape `{id?: string}` was incorrect — at runtime
 * `result.id` was undefined, which silently broke any consumer that
 * tried to chain a follow-up mutation against the new document id
 * (Lab linkage `ReceivedLabResults` call hit this 2026-05-21).
 *
 * Returning `string` directly aligns the static type with the wire.
 */
export function useSaveDocument() {
  const qc = useQueryClient();
  return useMutation<string, Error, PatientDocumentModel>({
    mutationFn: (body) =>
      post<string>('/api/Patients/AddUpdatePatientDocument', body),
    onSuccess: (_id, body) => {
      // PROD F-DOC-032: fire the "Doc Added" Mixpanel event on a successful
      // save, mirroring Flutter `document_bloc.dart:125-134`
      // (MixPanelEvents.DOC_ADDED → "Doc Added"). Props verbatim: org id/name +
      // encounter_id. PHI rule: identifiers only, no names/MRN. React's session
      // doesn't persist the org NAME, so organization_name is null.
      track('Doc Added', {
        organization_id: useAuthStore.getState().user?.organizationId ?? null,
        organization_name: null,
        encounter_id: body.encounterId ?? '',
      });
      // Invalidate the list query so the Docs tab refetches. We
      // can't be more granular yet because the list cache is keyed
      // on multiple filter params — invalidating the root prefix
      // covers every active list view of this patient's documents.
      void qc.invalidateQueries({ queryKey: ['patient-documents'] });
    },
  });
}
